import React from 'react';
import {
    survey1, survey2, survey3, survey4,
    persona, sketch, lofi, hifi,
    before1, before2, before3, before4, before5, before6,
    after1, after2, after3, after5, after6
} from '../image_index';
import './index.scss';

export const sections = [
    {
        id: 'hem_empathize',
        title: 'Empathizing',
        content: (
            <div>
                <h3>User Discovery & Survey Insights</h3>
                <p>
                    We targeted UC Davis students and staff who were required to schedule mandatory health appointments every 4 to 14 days. To identify existing pain points, our team conducted a quantitative and qualitative user survey (N ≈ 30).
                </p>
                <p>
                    <b>Key Findings:</b> Users overwhelmingly expressed frustration with the repetitive nature of the legacy workflow, pointing out a lack of saved preferences, redundant confirmation steps, and inefficient date pickers.
                </p>
                
                <div>
                    <div className='case_study_section_grid'>
                        <img src={survey1} className='case_study_section_grid_img' alt='Survey result: scheduling frequency' />
                        <img src={survey2} className='case_study_section_grid_img' alt='Survey result: portal satisfaction' />
                    </div>
                    <div className='case_study_section_grid'>
                        <img src={survey3} className='case_study_section_grid_img' alt='Survey result: user friction points' />
                        <img src={survey4} className='case_study_section_grid_img' alt='Survey result: desired features' />
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 'hem_define',
        title: 'Define',
        content: (
            <div>
                <h3>Problem Statement</h3>
                <p>
                    <i>How might we streamline UC Davis’ Health-E-Messaging scheduling workflow to minimize redundant steps, improve navigation forgiveness, and optimize task completion time for recurring user visits?</i>
                </p>
                
                <h3>User Persona</h3>
                <p>
                    We synthesized survey data into a target user persona to guide our design decisions and establish usability benchmarks across our sprint cycles.
                </p>
                <div>
                    <img src={persona} className='SC' alt='Target User Persona' />
                </div>
            </div>
        )
    },
    {
        id: 'hem_ideate',
        title: 'Ideate',
        content: (
            <div>
                <h3>Design Exploration & Wireframing</h3>
                <p>
                    Beginning with rapid paper sketches, we mapped out revised user flows to consolidate redundant screens.
                </p>
                <div>
                    <img src={sketch} className='SC' alt='Initial paper sketches' />
                </div>

                <p>
                    We translated these concepts into low-fidelity Figma wireframes to test alternative scheduling layouts and selection patterns.
                </p>
                <div>
                    <img src={lofi} className='SC' alt='Low-fidelity Figma wireframes' />
                </div>

                <h3>High-Fidelity Interface Architecture</h3>
                <div>
                    <img src={hifi} className='SC' alt='High-fidelity component designs' />
                </div>

                <h3>Before vs. After Comparison</h3>
                <p>
                    Below is a direct comparison mapping the legacy interface screens (left) to our optimized Figma redesign (right):
                </p>
                <div className='case_study_comparison_grid'>
                    <div className='case_study_section_grid'>
                        <img src={before1} className='case_study_section_grid_img case_study_section_grid_img_before' alt='Legacy portal start page' />
                        <img src={after1} className='case_study_section_grid_img case_study_section_grid_img_after' alt='Redesigned portal start page' />
                    </div>
                    <div className='case_study_section_grid'>
                        <img src={before2} className='case_study_section_grid_img' alt='Legacy appointment selection' />
                        <img src={after2} className='case_study_section_grid_img case_study_section_grid_img_after' alt='Redesigned appointment selection' />
                    </div>
                    <div className='case_study_section_grid'>
                        <img src={before3} className='case_study_section_grid_img' alt='Legacy location picker' />
                        <img src={after3} className='case_study_section_grid_img case_study_section_grid_img_after' alt='Redesigned location picker' />
                    </div>
                    <div className='case_study_section_grid'>
                        <img src={before4} className='case_study_section_grid_img' alt='Legacy time slot picker' />
                        <img src={after3} className='case_study_section_grid_img case_study_section_grid_img_after' alt='Redesigned time slot picker' />
                    </div>
                    <div className='case_study_section_grid'>
                        <img src={before5} className='case_study_section_grid_img case_study_section_grid_img_before' alt='Legacy user details form' />
                        <img src={after5} className='case_study_section_grid_img case_study_section_grid_img_after' alt='Redesigned user details form' />
                    </div>
                    <div className='case_study_section_grid'>
                        <img src={before6} className='case_study_section_grid_img case_study_section_grid_img_before' alt='Legacy confirmation screen' />
                        <img src={after6} className='case_study_section_grid_img case_study_section_grid_img_after' alt='Redesigned confirmation screen' />
                    </div>
                </div>

                <h3>Key Usability Improvements</h3>
                <ul>
                    <li><b>Workflow Consolidation:</b> Reduced overall task steps from 11 screens/pop-ups down to 5 streamlined pages.</li>
                    <li><b>Progress Transparency:</b> Integrated a persistent progress bar indicating current step and remaining effort.</li>
                    <li><b>User Forgiveness & Elasticity:</b> Added universal back navigation, preventing accidental session resets.</li>
                    <li><b>Data Persistence:</b> Enabled optional profile saving to auto-fill recurring user information.</li>
                    <li><b>Visual Scheduling:</b> Replaced restrictive drop-downs with a responsive, calendar-style slot picker.</li>
                    <li><b>Micro-Interactions & Affordance:</b> Implemented clear hover states, active button indicators, and inline validation messages.</li>
                </ul>
            </div>
        )
    },
    {
        id: 'hem_prototype',
        title: 'Prototype',
        content: (
            <div>
                <h3>Frontend Implementation</h3>
                <p>
                    Transitioning from Figma to code, our team implemented a high-fidelity web prototype using <b>React</b>. Given course time constraints, we prioritized engineering the interactive calendar and time slot selection engine—the core bottleneck of the original user experience.
                </p>
            </div>
        )
    },
    {
        id: 'hem_test',
        title: 'Takeaways',
        content: (
            <div>
                <h3>Evaluative Insights</h3>
                <p>
                    While preliminary A/B variants of the calendar UI were evaluated during the design phase, formal usability testing on the compiled React build remains a valuable next step to quantify task-time reduction.
                </p>

                <h3>Key Takeaways</h3>
                <ul>
                    <li><b>Cross-Functional Collaboration:</b> Partnered across a 6-person team to align UX research, interface specs, and React engineering goals.</li>
                    <li><b>Design System Consistency:</b> Standardized reusable UI components across both Figma and React libraries to maintain layout parity.</li>
                </ul>
            </div>
        )
    }
];